import * as Sentry from "@sentry/react"
import {
    AccountModel,
    CustomerModel,
    EEmailProviders,
    EOperatorTypes,
    findNestedProperty,
    formatNumber,
    TEmailReport,
    TTimeInterval
} from "@cakemail-org/ui-components-v2"
import { t } from "i18next"
import { parseIntervalToUnix } from "../date"
import { ELogErrorMsg } from "./types"

export function logError(message: ELogErrorMsg, extra = {}) {
    const formattedExtra = typeof extra === "string" ? { msg: extra } : extra
    if (process.env.NODE_ENV === "development")
        return console.log(message, formattedExtra) // eslint-disable-line no-console
    Sentry.captureEvent({ message, extra: formattedExtra })
}

export function affixToPercent(stat?: number, language?: string): string {
    if (stat) {
        const fixedNumber = stat.toFixed(2)
        return formatNumber(fixedNumber, language) + "%"
    }
    return "0%"
}

export function abbreviateNumber(value: number, decimals: number = 1): string {
    const suffixes = ['', t("general-abreviation-thousand"), t("general-abreviation-million"), t("general-abreviation-billion"), t("general-abreviation-trillion")]

    // Handle small numbers (no abbreviation needed)
    if (Math.abs(value) < 1000) {
        return value.toString()
    }

    // Calculate the exponent (logarithmic base 1000)
    const exponent = Math.floor(Math.log10(Math.abs(value)) / 3)
    const suffix = suffixes[exponent] || ''
    const abbreviatedValue = (value / Math.pow(1000, exponent)).toFixed(decimals)

    return `${abbreviatedValue}${suffix}`
}


export function fillMissingReportIntervals(
    logs: TEmailReport[],
    startDate: number,
    endDate: number,
    interval: TTimeInterval,
    emptyObj: any
) {
    const intervalInSeconds = parseIntervalToUnix(interval)
    // find the anchor, which is the first unix timestamp modulo intervalInSeconds after startDate
    const anchor = startDate + (intervalInSeconds - (startDate % intervalInSeconds))

    // verify that the intervals are all aligned to the anchor (interval_start % intervalInSeconds with === 0) and have an expected end (interval_start + intervalInSeconds)
    if (logs.some((log) => log.interval_start % intervalInSeconds !== 0 || log.interval_end !== log.interval_start + intervalInSeconds)) {
        throw new Error("fillMissingReportIntervals: logs are not aligned to the interval and cannot be filled")
    }

    // Fill missing intervals
    for (let interval = anchor; interval < endDate; interval += intervalInSeconds) {
        const matchingLog = logs.find((log) => log.interval_start === interval)

        if (!matchingLog) {
            //console.log(`Filled log for interval ${interval} to ${interval + intervalInSeconds}`)
            logs.push({
                ...emptyObj,
                interval_start: interval,
                interval_end: interval + intervalInSeconds
            })
        }
    }

    // Sort logs by interval_start
    logs.sort((a, b) => a.interval_start - b.interval_start)
}

export function mergeArrayWithReduce<T>(objects: T[]): T {
    const result = objects.reduce(
        (acc: any, obj: any) => {
            Object.keys(obj).forEach((key) => {
                if (key.endsWith("_rate")) {
                    // For "_rate" properties, accumulate the sum and increment the count for averaging
                    acc[key] = (acc[key] || 0) + obj[key]
                    acc["_rateCounts"] = acc["_rateCounts"] || {}
                    acc["_rateCounts"][key] = (acc["_rateCounts"][key] || 0) + 1
                } else {
                    acc[key] = (acc[key] || 0) + obj[key]
                }
            })
            return acc
        },
        { _rateCounts: {} } as any // Initialize with an empty object to store counts for "_rate"
    )

    // Final post-processing: calculate averages for "_rate" properties
    Object.keys(result["_rateCounts"]).forEach((key) => {
        result[key] = result[key] / result["_rateCounts"][key]
    })
    delete result["_rateCounts"]

    return result
}


export function returnProviderQuery({ providers, defaultProviders, addDefaultsToEmpty = false, operator = EOperatorTypes.and }: { providers?: EEmailProviders | EEmailProviders[], defaultProviders?: EEmailProviders[], addDefaultsToEmpty?: boolean, operator?: EOperatorTypes }) {
    if (!providers) {
        return undefined
    }

    if (!defaultProviders) {
        defaultProviders = [EEmailProviders.gmail, EEmailProviders.outlook, EEmailProviders.proton, EEmailProviders.aol, EEmailProviders.yahoo, EEmailProviders.icloud, EEmailProviders.other]
    }

    const currentOperator = operator || EOperatorTypes.and

    if (Array.isArray(providers) && !(providers.length === 1 && providers[0] === EEmailProviders.other)) {
        if (providers.includes(EEmailProviders.other)) {
            return {
                [currentOperator]: [
                    { [currentOperator]: providers.filter(provider => provider !== EEmailProviders.other) },
                    {
                        not: {
                            [currentOperator]: defaultProviders.filter(provider => provider !== EEmailProviders.other)
                        }
                    }
                ]
            }
        } else if (providers.length === 0) {
            if (addDefaultsToEmpty) {
                return {
                    [currentOperator]: defaultProviders.filter(provider => provider !== EEmailProviders.other)
                }
            } else {
                return undefined
            }
        }

        return { [currentOperator]: providers }
    } else if (providers === EEmailProviders.other || (Array.isArray(providers) && providers[0] === EEmailProviders.other)) {
        return {
            not: {
                [currentOperator]: defaultProviders.filter(provider => provider !== EEmailProviders.other)
            }
        }
    }
    return { is: providers }
}

export function returnEmailTagsQuery(tagFilter: null, options?: { operator?: EOperatorTypes }): undefined
export function returnEmailTagsQuery(tagFilter: string, options?: { operator?: EOperatorTypes }): { is: string }
export function returnEmailTagsQuery(tagFilter: Array<any>, options?: { operator?: EOperatorTypes }): object
export function returnEmailTagsQuery(tagFilter: any, options?: { operator?: EOperatorTypes }): undefined | object {
    if (tagFilter == null) {
        return undefined
    }

    if (Array.isArray(tagFilter)) {
        if (tagFilter.length === 0) {
            return undefined
        }
        return { [options?.operator || EOperatorTypes.and]: tagFilter }
    }

    if (typeof tagFilter === "object") {
        return tagFilter
    }

    return { is: tagFilter }
}


export function getProvidersToFetch(providerFilter?: EEmailProviders[]) {
    let defaultProviders: EEmailProviders[] = [EEmailProviders.gmail, EEmailProviders.outlook, EEmailProviders.proton, EEmailProviders.aol, EEmailProviders.yahoo, EEmailProviders.icloud, EEmailProviders.other]

    if (!providerFilter || providerFilter.length === 0) {
        return defaultProviders
    }

    return providerFilter
}


export function getPlanNames({ client, customer, getString = false }: { client: AccountModel, customer?: CustomerModel, getString?: boolean }): string[] {
    let keys = ["no_subscription"]
    if (client.partner) {
        keys = ["multi_account"]
    } else if (!customer) {
        keys = ["no_customer"]
    }
    else if (customer) {
        if (!customer.subscriptions) {
            keys = ["no_subscription"]
        } else if (customer.subscriptions) {
            keys = []
            customer.subscriptions.forEach(sub => {
                const cat = findNestedProperty(sub.items, "category")
                if (sub.legacy) {
                    keys.push("legacy")
                } else if (cat) {
                    keys.push(cat)
                } else {
                    keys.push("other")
                }
            })
        }
    }

    if (getString) {
        return keys
    }
    return keys.map((k) => t(`plan-name-${k}`))
}